import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { BROWN, NEUTRAL, RADIUS } from "../../styles/brandTokens";
import type { TheoOverviewTechnician } from "../../types/integration";
import { formatCount, text } from "../monitor/SummaryPanel";
import { formatHours } from "./theoOverviewModel";

const headCellSx = {
  ...text(11.5, NEUTRAL.textSecondary, 700),
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  borderBottom: `1px solid ${NEUTRAL.border}`,
  py: 1,
  whiteSpace: "nowrap" as const,
};
const cellSx = { ...text(13.5), borderBottom: `1px solid ${NEUTRAL.border}`, py: 1.25 };
const numberSx = { ...cellSx, textAlign: "right" as const, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" as const };

export function formatLastActive(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

interface TechniciansDialogProps {
  open: boolean;
  onClose: () => void;
  technicians: TheoOverviewTechnician[];
  /** The slice the list belongs to, e.g. "Last 30 days · Contoso". */
  scopeLabel: string;
}

const TechniciansDialog: React.FC<TechniciansDialogProps> = ({ open, onClose, technicians, scopeLabel }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    aria-labelledby="technicians-title"
    PaperProps={{ sx: { borderRadius: `${RADIUS.well}px` } }}
  >
    <DialogTitle id="technicians-title" sx={{ ...text(20, NEUTRAL.text, 700), pr: 6, pb: 0.5 }}>
      Technicians who worked with Theo
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: "absolute", right: 12, top: 12, color: NEUTRAL.textSecondary, borderRadius: `${RADIUS.control}px` }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent>
      <Typography sx={{ ...text(13.5, NEUTRAL.textSecondary), mb: 2 }}>{scopeLabel}</Typography>
      {technicians.length === 0 ? (
        <Typography sx={text(13.5, NEUTRAL.textMuted)}>No technician worked with Theo in this view.</Typography>
      ) : (
        <Box sx={{ overflowX: "auto", mx: -1 }}>
          <Table size="small" aria-label="Technicians who worked with Theo" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Technician</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Tickets</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Chat Assistant</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Actions</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Time saved</TableCell>
                <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Last active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {technicians.map((person) => (
                <TableRow key={person.email}>
                  <TableCell sx={{ ...cellSx, ...text(13.5, NEUTRAL.text, 600), wordBreak: "break-all" }}>{person.email}</TableCell>
                  <TableCell sx={numberSx}>{formatCount(person.tickets)}</TableCell>
                  <TableCell sx={numberSx}>{formatCount(person.conversations)}</TableCell>
                  <TableCell sx={numberSx}>{formatCount(person.actions)}</TableCell>
                  <TableCell sx={{ ...numberSx, ...text(13.5, BROWN.main, 700) }}>
                    {person.actions ? formatHours(person.estimated_minutes) : "—"}
                  </TableCell>
                  <TableCell sx={{ ...numberSx, color: NEUTRAL.textSecondary }}>{formatLastActive(person.last_active_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      <Typography sx={{ ...text(12.5, NEUTRAL.textMuted), mt: 2 }}>
        Lists this MSP's current Theo accounts that typed to Theo, in a ticket or in Chat Assistant. Theo staff are
        left out entirely. Removed accounts and test IDs aren't listed, though their tickets still count in the totals.
      </Typography>
      <Typography sx={{ ...text(12.5, NEUTRAL.textMuted), mt: 1 }}>
        A ticket counts for an agent if an agent worked it, and for a technician if they typed to Theo on it, so a ticket
        an agent handed off to a technician counts for both. Each action counts once, for whoever ran the turn it was
        part of: actions after a technician approves an agent's step stay with the agent.
      </Typography>
    </DialogContent>
  </Dialog>
);

export default TechniciansDialog;
